import type {
  BaseStorage,
  BaseStorageOptions,
} from '@wix/fe-essentials/data-capsule';
import { isHttpError } from '../util/isHttpError';
import { WixPatternsEssentials } from '../model';

export interface WixStorageStrategyOptions extends BaseStorageOptions {
  expiration: number;
}

export interface AccountPreferencesStrategyParams {
  readonly httpClient: WixPatternsEssentials['httpClient'];
}

export class AccountPreferencesStrategy
  implements BaseStorage<WixStorageStrategyOptions>
{
  private readonly httpClient;

  readonly accountPrefHttpPath =
    '/_api/app-settings-service/v1/accountPreferences';

  constructor(params: AccountPreferencesStrategyParams) {
    this.httpClient = params.httpClient;
  }

  extendScope(scope: Required<WixStorageStrategyOptions>['scope']) {
    return scope;
  }

  async getAllItems<T>(options: WixStorageStrategyOptions): Promise<T> {
    const url = [
      this.accountPrefHttpPath,
      'getVolatilePrefs',
      options.namespace,
    ]
      .filter((x) => x)
      .join('/');

    const { data } = await this.httpClient.request<T>({
      url,
      method: 'GET',
    });
    return data;
  }

  async getItem<T>(
    key: string,
    options: WixStorageStrategyOptions,
  ): Promise<T> {
    const url = [
      this.accountPrefHttpPath,
      'getVolatilePrefForKey',
      options.namespace,
      key,
    ]
      .filter((x) => x)
      .join('/');

    try {
      const { data } = await this.httpClient.request<Record<string, T>>({
        url,
        method: 'GET',
      });
      return data[key];
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 404) {
        throw new Error('Key was not found in capsule');
      }
      throw e;
    }
  }

  async removeItem(key: string, options: WixStorageStrategyOptions) {
    const payload = {
      namespace: options.namespace,
      key,
    };

    const url = [this.accountPrefHttpPath, 'delete'].filter((x) => x).join('/');

    await this.httpClient.request({ url, data: payload, method: 'POST' });
  }

  async setItem<T>(key: string, value: T, options: WixStorageStrategyOptions) {
    const payload: any = {
      namespace: options.namespace,
      key,
      blob: value,
    };

    if (options.expiration) {
      payload.ttlInDays = Math.ceil(options.expiration / (60 * 60 * 24));
    }

    const url = [this.accountPrefHttpPath, 'set'].filter((x) => x).join('/');

    await this.httpClient.request({ url, data: payload, method: 'POST' });
  }
}
